
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InstallMobileRoundedIcon from "@mui/icons-material/InstallMobileRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { lazy, Suspense, useState, useSyncExternalStore, type ReactNode } from "react";
import { APP_VERSION_DISPLAY } from "../appVersion";
import { useAppUpdate } from "../hooks/useAppUpdate";
import { getLastKnownAuthUser } from "../services/authService";
import { getRuntimeActivitySnapshot, subscribeRuntimeActivity } from "../services/runtimeActivityService";

const VersionHistorySheet = lazy(() => import("./VersionHistorySheet"));
const AdminRuntimeActivitySheet = lazy(() => import("./AdminRuntimeActivitySheet"));

export type StartSection =
  | "history"
  | "allocations"
  | "masterData"
  | "settings"
  | "admin"
  | "visualLab"
  | "visionOptimizer";

interface StartPageProps {
  onStartWithdrawal: () => void;
  onOpenSection: (section: StartSection) => void;
}

const SECONDARY_ACTIONS: Array<{
  id: StartSection;
  label: string;
  icon: ReactNode;
}> = [
  { id: "history", label: "Verlauf", icon: <HistoryRoundedIcon /> },
  { id: "allocations", label: "Kontierungen", icon: <ViewWeekOutlinedIcon /> },
  { id: "masterData", label: "Materialstammliste", icon: <StorageRoundedIcon /> },
  { id: "settings", label: "Einstellungen", icon: <SettingsOutlinedIcon /> },
];

export default function StartPage({
  onStartWithdrawal,
  onOpenSection,
}: StartPageProps) {
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [runtimeOpen, setRuntimeOpen] = useState(false);
  const isAdmin = getLastKnownAuthUser()?.role === "admin";
  const runtime = useSyncExternalStore(subscribeRuntimeActivity, getRuntimeActivitySnapshot, getRuntimeActivitySnapshot);
  const { available: appUpdateAvailable, applying: appUpdateApplying, phase: appUpdatePhase, error: appUpdateError, restartRequired: appUpdateRestartRequired, coordinatedByOtherTab: appUpdateCoordinatedByOtherTab, applyUpdate } = useAppUpdate();
  const appUpdateLabel = appUpdateApplying
    ? appUpdatePhase === "activating"
      ? "Update wird aktiviert …"
      : appUpdatePhase === "reloading"
        ? "App wird neu geladen …"
        : "Update wird vorbereitet …"
    : appUpdateRestartRequired
      ? "Neue App-Version aktiv · Jetzt neu laden"
      : appUpdateCoordinatedByOtherTab
        ? "Update wird in anderem Tab vorbereitet …"
        : appUpdateError
          ? "Update noch nicht bereit · erneut versuchen"
          : "Neue App Version verfügbar · Jetzt aktualisieren";

  return (
    <>
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 620,
        mx: "auto",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        pb: "calc(1.65rem + env(safe-area-inset-bottom))",
      }}
    >
      <Stack
        sx={{
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            pt: 0.35,
            flex: "0 0 auto",
          }}
        >
          <Box
            component="img"
            src="/icons/materialentnahme-logo-512.png"
            alt="Materialentnahme Logo"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            sx={{
              display: "block",
              width: { xs: 112, sm: 124 },
              height: { xs: 112, sm: 124 },
              objectFit: "contain",
              mx: "auto",
              mb: 0.25,
              filter: "drop-shadow(0 9px 20px rgba(0,0,0,0.28))",
              "@media (max-height: 720px)": {
                width: appUpdateAvailable ? 88 : 94,
                height: appUpdateAvailable ? 88 : 94,
                mb: 0,
              },
              "@media (max-height: 640px)": {
                width: appUpdateAvailable ? 74 : 80,
                height: appUpdateAvailable ? 74 : 80,
              },
            }}
          />

          <Typography
            variant="h4"
            sx={{
              color: "#d90039",
              fontWeight: 900,
              fontSize: { xs: "1.82rem", sm: "2rem" },
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              "@media (max-height: 680px)": {
                fontSize: "1.55rem",
              },
            }}
          >
            Materialentnahme
          </Typography>
        </Box>

        <Stack
          spacing={appUpdateAvailable ? 0.65 : 0.95}
          sx={{
            mt: appUpdateAvailable ? { xs: 3.85, sm: 4.35 } : { xs: 5.0, sm: 5.7 },
            flex: "0 0 auto",
            "@media (max-height: 720px)": {
              mt: 3.25,
            },
            "@media (max-height: 680px)": {
              mt: 2.45,
              gap: appUpdateAvailable ? 0.5 : 0.65,
            },
          }}
        >
          <Button
            variant="contained"
            onClick={onStartWithdrawal}
            sx={{
              minHeight: 50,
              px: 1.15,
              borderRadius: "9px",
              textTransform: "none",
              fontSize: "0.98rem",
              fontWeight: 850,
              color: "#07171d",
              background: "linear-gradient(180deg, #74dcef 0%, #5bcbe4 100%)",
              border: "1px solid rgba(159,236,251,.76)",
              boxShadow: "0 5px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.22)",
              transition: "background-color 140ms ease, border-color 140ms ease, transform 90ms ease",
              "&:hover": {
                background: "linear-gradient(180deg, #82e1f2 0%, #65d1e8 100%)",
                borderColor: "rgba(184,244,255,.88)",
                boxShadow: "0 5px 18px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.25)",
              },
              "&:active": {
                transform: "translateY(1px)",
                background: "linear-gradient(180deg, #62cee6 0%, #52c3dc 100%)",
              },
              "@media (max-height: 680px)": {
                minHeight: appUpdateAvailable ? 42 : 46,
                fontSize: "0.92rem",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "36px minmax(0,1fr) 26px",
                alignItems: "center",
                columnGap: 1.1,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 19, justifySelf: "center" }} />
              <Box component="span" sx={{ textAlign: "left", lineHeight: 1.15 }}>Neue Materialentnahme starten</Box>
              <ChevronRightRoundedIcon sx={{ fontSize: 20, justifySelf: "center" }} />
            </Box>
          </Button>

          {SECONDARY_ACTIONS.map((action) => (
            <Button
              key={action.id}
              variant="outlined"
              onClick={() => onOpenSection(action.id)}
              sx={{
                minHeight: appUpdateAvailable ? 44 : 48,
                px: 1.15,
                borderRadius: "9px",
                textTransform: "none",
                fontSize: "0.98rem",
                fontWeight: 800,
                color: "#f2f4f6",
                borderColor: "rgba(174,190,199,.38)",
                bgcolor: "rgba(18,21,24,.95)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,.03)",
                position: "relative",
                overflow: "hidden",
                transition: "background-color 140ms ease, border-color 140ms ease, transform 90ms ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  borderRadius: "0 2px 2px 0",
                  bgcolor: "#58d8f3",
                  opacity: 0,
                  transition: "opacity 140ms ease",
                },
                "&:hover": {
                  borderColor: "rgba(99,217,242,.64)",
                  bgcolor: "rgba(24,29,33,.98)",
                },
                "&:hover::before, &:focus-visible::before": { opacity: 0.72 },
                "&:focus-visible": {
                  outline: "2px solid rgba(88,216,243,.34)",
                  outlineOffset: 1,
                },
                "&:active": {
                  transform: "translateY(1px)",
                  bgcolor: "rgba(27,33,37,.99)",
                },
                "&:active::before": { opacity: 1 },
                "@media (max-height: 680px)": {
                  minHeight: appUpdateAvailable ? 40 : 44,
                  fontSize: "0.92rem",
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "36px minmax(0,1fr) 26px",
                  alignItems: "center",
                  columnGap: 1.1,
                }}
              >
                <Box component="span" sx={{ display: "grid", placeItems: "center", color: "#58d8f3", "& svg": { fontSize: 19 } }}>{action.icon}</Box>
                <Box component="span" sx={{ textAlign: "left", lineHeight: 1.15 }}>{action.label}</Box>
                <ChevronRightRoundedIcon sx={{ fontSize: 20, justifySelf: "center", color: "rgba(203,213,218,.72)" }} />
              </Box>
            </Button>
          ))}

          {appUpdateAvailable && (
            <Button
              variant="outlined"
              startIcon={appUpdateApplying ? <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} /> : <InstallMobileRoundedIcon sx={{ fontSize: 18 }} />}
              disabled={appUpdateApplying || appUpdateCoordinatedByOtherTab}
              onClick={() => { void applyUpdate(); }}
              sx={{
                minHeight: 34,
                py: 0.35,
                borderRadius: "9px",
                textTransform: "none",
                fontSize: "0.78rem",
                fontWeight: 900,
                color: "#e6eef1",
                borderColor: "rgba(131,206,224,.42)",
                bgcolor: "rgba(18,21,24,.95)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,.025)",
                "& .MuiButton-startIcon": { color: "#58d8f3" },
                "&:hover": { borderColor: "#83cee0", bgcolor: "rgba(24,29,33,.98)" },
                "@media (max-height: 680px)": { minHeight: 30, fontSize: "0.72rem" },
              }}
            >
              {appUpdateLabel}
            </Button>
          )}
          {appUpdateAvailable && appUpdateError && !appUpdateApplying && (
            <Typography
              variant="caption"
              sx={{
                mt: "0 !important",
                px: 1,
                color: "#f4a340",
                fontSize: "0.67rem",
                lineHeight: 1.2,
                textAlign: "center",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {appUpdateError}
            </Typography>
          )}
        </Stack>
      </Stack>

      {isAdmin && (
        <Button
          size="small"
          variant="text"
          startIcon={<TerminalRoundedIcon sx={{ fontSize: 15 }} />}
          onClick={() => setRuntimeOpen(true)}
          sx={{
            position: "absolute",
            left: 0,
            bottom: "max(0.05rem, env(safe-area-inset-bottom))",
            minWidth: 0,
            minHeight: 28,
            px: 0.8,
            borderRadius: 999,
            textTransform: "none",
            fontSize: "0.68rem",
            fontWeight: 850,
            color: runtime.activeCount || runtime.queuedCount ? "#83cee0" : "#777980",
            bgcolor: runtime.activeCount || runtime.queuedCount ? "rgba(131,206,224,.055)" : "transparent",
            "& .MuiButton-startIcon": { mr: 0.45 },
          }}
        >
          Runtime{runtime.activeCount || runtime.queuedCount ? ` · ${runtime.activeCount + runtime.queuedCount}` : ""}
        </Button>
      )}

      <Box
        component="button"
        type="button"
        onClick={() => setVersionHistoryOpen(true)}
        aria-label={`Versionsverlauf öffnen, aktuelle Version ${APP_VERSION_DISPLAY}`}
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "max(0.2rem, env(safe-area-inset-bottom))",
          border: 0,
          p: "2px 8px",
          bgcolor: "transparent",
          color: "#777980",
          font: "inherit",
          cursor: "pointer",
          borderRadius: 999,
          WebkitTapHighlightColor: "transparent",
          "&:active": { bgcolor: "rgba(131,206,224,.08)", color: "#83cee0" },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{APP_VERSION_DISPLAY}</Typography>
      </Box>
    </Box>
    {versionHistoryOpen && (
      <Suspense fallback={null}>
        <VersionHistorySheet open onClose={() => setVersionHistoryOpen(false)} />
      </Suspense>
    )}
    {isAdmin && runtimeOpen && (
      <Suspense fallback={null}>
        <AdminRuntimeActivitySheet open onClose={() => setRuntimeOpen(false)} />
      </Suspense>
    )}
    </>
  );
}
